/**
 * Auth hook backed by the Flask `/auth/status` endpoint.
 *
 * Single source of truth is a TanStack Query cache keyed under `AUTH_QUERY_KEY`.
 * Feature code reads it via {@link useAuth}; the query client is exported via
 * helpers so navigation / logout / 401-interceptor paths can invalidate without
 * dragging React refs around.
 *
 * Shape is stable so Tier-2 feature units can depend on it.
 */
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiGet, ApiError } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';

export interface AuthUser {
  email: string;
  groups: string[];
  preferredUsername?: string;
  organization?: string;
}

/** Raw shape returned by `GET /auth/status`. Kept internal — UI uses {@link AuthUser}. */
interface AuthStatusResponseRaw {
  authenticated: boolean;
  user?: {
    email?: string | null;
    groups?: string[] | null;
    preferred_username?: string | null;
    organization?: string | null;
  };
  user_role?: string;
  is_superuser?: boolean;
  is_analytics_admin?: boolean;
  is_fulcrum_internal?: boolean;
  portfolio_funds?: string[] | null;
  auth_method?: string;
}

export interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isAnalyticsAdmin: boolean;
  isFulcrumInternal: boolean;
  isSuperuser: boolean;
  /** Active fund IDs this user may access — from the backend's `funds_for`. */
  portfolioFunds: readonly string[];
  userRole: string | null;
  authMethod: string | null;
}

export interface UseAuthResult extends AuthState {
  isLoading: boolean;
  error: Error | null;
}

export const AUTH_QUERY_KEY = ['auth', 'status'] as const;

const UNAUTHENTICATED: AuthState = {
  user: null,
  isAuthenticated: false,
  isAnalyticsAdmin: false,
  isFulcrumInternal: false,
  isSuperuser: false,
  portfolioFunds: [],
  userRole: null,
  authMethod: null,
};

function normalize(raw: AuthStatusResponseRaw | undefined): AuthState {
  if (!raw || !raw.authenticated || !raw.user?.email) {
    return UNAUTHENTICATED;
  }
  return {
    user: {
      email: raw.user.email,
      groups: raw.user.groups ?? [],
      preferredUsername: raw.user.preferred_username ?? undefined,
      organization: raw.user.organization ?? undefined,
    },
    isAuthenticated: true,
    isAnalyticsAdmin: Boolean(raw.is_analytics_admin),
    isFulcrumInternal: Boolean(raw.is_fulcrum_internal),
    isSuperuser: Boolean(raw.is_superuser),
    portfolioFunds: raw.portfolio_funds ?? [],
    userRole: raw.user_role ?? null,
    authMethod: raw.auth_method ?? null,
  };
}

export async function fetchAuthStatus(): Promise<AuthStatusResponseRaw> {
  try {
    // Do not trigger the shared 401 redirect — unauthenticated is a valid state.
    return await apiGet<AuthStatusResponseRaw>(endpoints.auth.status(), { redirectOn401: false });
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      return { authenticated: false };
    }
    throw err;
  }
}

export function useAuth(): UseAuthResult {
  const query = useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: fetchAuthStatus,
    // Re-validate the session when the user returns to the tab (or the
    // network reconnects). The Flask session is an 8h idle timeout
    // (PERMANENT_SESSION_LIFETIME); without this, a tab left open past 8h
    // only discovers the dead session on the next data fetch — showing a
    // page of failed loads. A finite staleTime is required for the focus
    // refetch to fire (Infinity would keep the query permanently "fresh").
    // `fetchAuthStatus` maps a 401 to { authenticated: false } without
    // triggering the redirect interceptor, so an expired session flips
    // RequireAuth to a single clean redirect to /login. Actively-used tabs
    // get the side benefit of keeping the sliding session alive.
    staleTime: 5 * 60_000,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const state = normalize(query.data);

  return {
    ...state,
    isLoading: query.isLoading,
    error: (query.error as Error | null) ?? null,
  };
}

/** Invalidate the auth cache — used by logout and the 401 interceptor. */
export function invalidateAuth(client: QueryClient): Promise<void> {
  return client.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
}

/** Synchronous reset — sets cache to unauthenticated without an extra fetch. */
export function resetAuth(client: QueryClient): void {
  client.setQueryData<AuthStatusResponseRaw>(AUTH_QUERY_KEY, { authenticated: false });
}

/** Hook form for components that need to kick the cache after a mutation. */
export function useInvalidateAuth(): () => Promise<void> {
  const client = useQueryClient();
  return () => invalidateAuth(client);
}
